import { useMemo, useState } from "react";
import { Box, Heading, Tooltip, Icon, HStack, Text } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import FilterForm from "../../components/FilterForm";
import Loader from "../../components/loader";
import ServiceTimePerService from "../../components/organization/reports/ServiceTimePerService";
import { getReportDescription, getReportRangeString } from "../../store/helpers";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import useReportRenderSettingLoader from "../../hooks/useReportRenderSettingLoader";
import {
  DEFAULT_RENDER_CONFIG,
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  getRenderConfigSummaryItems,
  normalizeRenderConfig,
} from "../../store/renderConfig";
import { saveReportRenderSetting } from "../../store/actions/defaultAction";
import { downloadServiceTimePerServiceCsvFiles } from "../../store/serviceTimePerServiceExport";

const ServiceTimePerServicePage = ({ showFilterForm = true, showTitle = 1 }) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.authReducer);
  const {
    loading,
    reportRange,
    reportsData,
    branchId,
    organizationId,
    savingReportRenderSetting,
  } = useSelector((state) => state.defaultReducer);
  const [renderConfig, setRenderConfig] = useState(DEFAULT_RENDER_CONFIG);

  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_branch_service_time_per_service
      : REPORT_TYPE_IDS.monthly_branch_service_time_per_service;

  const data = useMemo(() => {
    let reports = reportsData.filter((item) => item.reportTypeId === reportTypeId);
    if (branchId.length > 0) {
      reports = reports.filter((item) => item.branchId === branchId);
    }
    return reports.map((item) => item.data) || [];
  }, [branchId, reportTypeId, reportsData]);
  const renderConfigOptions = useMemo(
    () => ({ columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS }),
    []
  );
  const isLoadingRenderSetting = useReportRenderSettingLoader({
    userId: userData?.id,
    organizationId,
    reportTypeId,
    renderConfigOptions,
    setRenderConfig,
  });
  const effectiveRenderConfig = useMemo(
    () => normalizeRenderConfig(renderConfig, renderConfigOptions),
    [renderConfig, renderConfigOptions]
  );
  const presentationSummaryItems = useMemo(
    () => getRenderConfigSummaryItems(effectiveRenderConfig, renderConfigOptions),
    [effectiveRenderConfig, renderConfigOptions]
  );
  const csvScopes = useMemo(
    () =>
      data.map((item) => ({
        scope: "Branch",
        branchName: item.branchName,
        data: item.servicesData,
      })),
    [data]
  );

  const saveRenderConfig = () => {
    dispatch(
      saveReportRenderSetting({
        userId: userData.id,
        organizationId,
        reportTypeId,
        renderConfig: effectiveRenderConfig,
      })
    );
  };

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>{getReportRangeString(reportRange)} Branch Service Time Per Service</title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : null}
      {loading || isLoadingRenderSetting ? <Loader /> : !_.isEmpty(data) ? (
        <>
          <Box mb="20px">
            <HStack flexDirection="row" justifyContent="space-between">
              <Heading fontSize="34px" color="#222" fontWeight="700">
                Service Time Per Service
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      {getReportDescription(reportRange).ServiceTimePerServiceBranches}
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              </Heading>
              <DownloadReport
                reportRange={reportRange}
                reportTypeId={reportTypeId}
                renderConfig={effectiveRenderConfig}
                onSave={saveRenderConfig}
                isSaving={savingReportRenderSetting}
                onDownloadCsv={() =>
                  downloadServiceTimePerServiceCsvFiles({
                    scopes: csvScopes,
                    renderConfig: effectiveRenderConfig,
                    filenamePrefix: "branch_service_time_per_service",
                  })
                }
              />
            </HStack>
          </Box>

          <ReportPresentationControls
            renderConfig={effectiveRenderConfig}
            onChange={setRenderConfig}
            columnDefinitions={SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS}
            defaultSortColumn="totalServiceTime"
          />
              <ReportPresentationSummary items={presentationSummaryItems} />

          {data.map((item, index) => (
            <ServiceTimePerService
              key={index}
              branchId={item.branchId}
              branchIndex={index + 1}
              data={item.servicesData}
              branchName={item.branchName}
              numServices={item.numServices}
              renderConfig={effectiveRenderConfig}
              onRenderConfigChange={setRenderConfig}
            />
          ))}
        </>
      ) : (
        <SelectBranchInfo message="Update Filters and Hit Search to retrieve data" />
      )}
    </Box>
  );
};

export default ServiceTimePerServicePage;
